import { first, isEmpty } from 'underscore';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createAsyncToken } from '@bingads-webui-universal/primitive-utilities';

export const makeUseOptionsAsyncDataSource = (loadOptions) => {
  const useOptionsAsyncDataSource = (searchFilter, optionsLimit = Number.MAX_SAFE_INTEGER) => {
    const isFirstLoad = useRef(true);
    const [isLoading, setIsLoading] = useState(true);
    const noDataAfterFirstLoad = useRef(false);
    const asyncTokenRef = useRef();

    const [isMoreOptionsThanLimit, setIsMoreOptionsThanLimit] = useState(false);
    const [options, setOptions] = useState([]);
    const [lastLoadedSearchFilter, setLastLoadedSearchFilter] = useState();
    const [loadError, setLoadError] = useState();

    const onDoneLoading = useCallback((loadedSearchFilter, newOptions) => {
      setIsLoading(false);
      noDataAfterFirstLoad.current = isFirstLoad.current && isEmpty(newOptions);
      isFirstLoad.current = false;
      setIsMoreOptionsThanLimit(newOptions.length > optionsLimit);
      setOptions(first(newOptions, optionsLimit));
      setLastLoadedSearchFilter(loadedSearchFilter);
      setLoadError(undefined);
      asyncTokenRef.current = null;
    }, [optionsLimit]);
    const onErrorLoading = useCallback((error) => {
      setIsLoading(false);
      setLoadError(error);
      asyncTokenRef.current = null;
    }, []);
    const onCancelLoading = useCallback(() => {
      setIsLoading(false);
      asyncTokenRef.current = null;
    }, []);

    useEffect(() => {
      if (asyncTokenRef.current) {
        asyncTokenRef.current.cancel();
      }
      setIsLoading(true);
      asyncTokenRef.current = createAsyncToken(loadOptions({
        searchFilter,
        top: optionsLimit + 1,
      })).on({
        onSuccess: (...args) => onDoneLoading(searchFilter, ...args),
        onError: onErrorLoading,
        onCancel: onCancelLoading,
      });

      return () => {
        if (asyncTokenRef.current) {
          asyncTokenRef.current.cancel();
        }
      };
    }, [searchFilter, optionsLimit, onDoneLoading, onErrorLoading, onCancelLoading]);

    return [isLoading, isFirstLoad.current, noDataAfterFirstLoad.current, isMoreOptionsThanLimit, options, loadError, lastLoadedSearchFilter];
  };

  return useOptionsAsyncDataSource;
};
